import { useState, useEffect } from "react";
import {
  getSelectedUid,
  getIsApproved,
  addUidListener,
  hasSelectedUid,
} from "./UidListener";

/**
 * Custom hook to manage selected UID state in components
 * @returns {Object} { uid, isApproved, hasUid, isLoading }
 */
export const useSelectedUid = () => {
  const [uid, setUid] = useState(() => getSelectedUid());
  const [isApproved, setIsApproved] = useState(() => getIsApproved());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set initial loading state
    setIsLoading(!hasSelectedUid());

    // Subscribe to UID changes
    const unsubscribe = addUidListener((newUid, approved) => {
      console.log(
        "[useSelectedUid] UID updated:",
        newUid,
        "Approved:",
        approved,
      );
      setUid(newUid);
      setIsApproved(approved);
      setIsLoading(false);
    });

    // Clean up subscription
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    uid,
    isApproved,
    hasUid: Boolean(uid),
    isLoading,
  };
};

/**
 * Custom hook to ensure a UID is selected before rendering
 * @param {Function} onNoUid - Callback when no UID is selected
 * @returns {Object} { uid, isApproved, isReady }
 */
export const useRequiredUid = (onNoUid) => {
  const { uid, isApproved, hasUid, isLoading } = useSelectedUid();

  useEffect(() => {
    if (!isLoading && !hasUid && onNoUid) {
      console.warn("[useRequiredUid] No UID selected");
      onNoUid();
    }
  }, [isLoading, hasUid, onNoUid]);

  return {
    uid,
    isApproved,
    isReady: !isLoading && hasUid,
  };
};
